import { TTypeOfCodec } from "../../../shared/src/codecs/codec";
import { array, TArrayCodec } from "../../../shared/src/codecs/types/array";
import { boolean, TAnyBooleanCodec } from "../../../shared/src/codecs/types/boolean";
import { form, TFormCodec } from "../../../shared/src/codecs/types/form";
import { intersection, TIntersectionCodec } from "../../../shared/src/codecs/types/intersection";
import { literal } from "../../../shared/src/codecs/types/literal";
import { nullCodec, TNullCodec } from "../../../shared/src/codecs/types/nullCodec";
import { positiveInteger } from "../../../shared/src/codecs/types/positiveInteger";
import { required, TRequiredCodec } from "../../../shared/src/codecs/types/required";
import { string, TAnyStringCodec } from "../../../shared/src/codecs/types/string";
import { TUnionCodec, union } from "../../../shared/src/codecs/types/union";
import { LandRegistryOfficialCopyWithSummaryResponse, LandRegistrySearchPropertiesResponseError, LandRegistrySearchPropertiesResponseOutOfHours, TLandRegistryOfficialCopyWithSummaryResponseCodec } from "../LandRegistry";
import { PublicPropertyReportAddressSearch, PublicPropertyReportCreate, PublicPropertyReportTitleSearch, TPublicPropertyReportAddressSearchCodec, TPublicPropertyReportCreateCodec, TPublicPropertyReportTitleSearchCodec } from "../PublicPropertyReport";
import { Quotes, TQuotesCodec } from "../Quote";
import { Tenure1, TTenure1Codec } from "../Tenure";

export const PublicPropertyReportCreateForm: TFormCodec<
    TIntersectionCodec<[
        TPublicPropertyReportCreateCodec,
        TRequiredCodec<{
            recaptcha_token: TAnyStringCodec
        }>
    ]>,
    TRequiredCodec<{
        read_only: TRequiredCodec<{
            tenure: TTenure1Codec,
            sub_building_name: TAnyStringCodec,
            building_name: TAnyStringCodec,
            building_number: TAnyStringCodec,
            street_name: TAnyStringCodec,
            city: TAnyStringCodec,
            postcode: TAnyStringCodec,
            full_address: TAnyStringCodec,
        }>
        response: TUnionCodec<[
            TNullCodec,
            TLandRegistryOfficialCopyWithSummaryResponseCodec,
        ]>,
        title_register_url: TAnyStringCodec,
        quotes: TQuotesCodec,
        is_valuation_only: TAnyBooleanCodec,
    }>
> = form(
    intersection([
        PublicPropertyReportCreate,
        required({
            recaptcha_token: string(),
        }),
    ]),
    required({
        read_only: required({
            tenure: Tenure1,
            sub_building_name: string(),
            building_name: string(),
            building_number: string(),
            street_name: string(),
            city: string(),
            postcode: string(),
            full_address: string(),
        }),
        response: union([
            nullCodec(),
            LandRegistryOfficialCopyWithSummaryResponse,
        ]),
        title_register_url: string(),
        quotes: Quotes,
        is_valuation_only: boolean(),
    }),
);
export type TPublicPropertyReportCreateFormCodec = typeof PublicPropertyReportCreateForm;
export type TPublicPropertyReportCreateForm = TTypeOfCodec<TPublicPropertyReportCreateFormCodec>;

const LandRegistrySearchPropertiesResponseSuccessOverride =
    required({
        _tag: literal("success"),
        results: array(PublicPropertyReportCreateForm),
    });
export type TLandRegistrySearchPropertiesResponseSuccessOverride = TTypeOfCodec<typeof LandRegistrySearchPropertiesResponseSuccessOverride>;

const PublicPropertyReportTitleSearchResponse = union([
    LandRegistrySearchPropertiesResponseSuccessOverride,
    LandRegistrySearchPropertiesResponseError,
    LandRegistrySearchPropertiesResponseOutOfHours,
]);
type TPublicPropertyReportTitleSearchResponseCodec = typeof PublicPropertyReportTitleSearchResponse;
export type TPublicPropertyReportTitleSearchResponse = TTypeOfCodec<TPublicPropertyReportTitleSearchResponseCodec>;

export const PublicPropertyReportTitleSearchForm: TFormCodec<
    TPublicPropertyReportTitleSearchCodec,
    TRequiredCodec<{
        response: TUnionCodec<[
            TNullCodec,
            TPublicPropertyReportTitleSearchResponseCodec
        ]>,
        address_string: TAnyStringCodec
    }>
> = form(
    PublicPropertyReportTitleSearch,
    required({
        response: union([
            nullCodec(),
            PublicPropertyReportTitleSearchResponse,
        ]),
        address_string: string(),
    }),
);
export type TPublicPropertyReportTitleSearchFormCodec = typeof PublicPropertyReportTitleSearchForm;
export type TPublicPropertyReportTitleSearchForm = TTypeOfCodec<TPublicPropertyReportTitleSearchFormCodec>;

export const PublicPropertyReportAddressSearchForm: TFormCodec<
    TPublicPropertyReportAddressSearchCodec,
    TRequiredCodec<{
        results: TArrayCodec<TPublicPropertyReportTitleSearchFormCodec>
    }>
> = form(
    PublicPropertyReportAddressSearch,
    required({
        results: array(PublicPropertyReportTitleSearchForm),
    })
);
export type TPublicPropertyReportAddressSearchFormCodec = typeof PublicPropertyReportAddressSearchForm;
export type TPublicPropertyReportAddressSearchForm = TTypeOfCodec<TPublicPropertyReportAddressSearchFormCodec>;

export const PublicPropertyReportPageView = union([
    literal("staff_introducer_select"),
    literal("signed_in_without_introducer"),
    literal("form_initial"),
    literal("form_transaction_type"),
    literal("form_executor_or_beneficiary"),
    literal("address_select"),
    literal("title_select"),
    literal("results"),
]);
export type TPublicPropertyReportPageView = TTypeOfCodec<typeof PublicPropertyReportPageView>;

export const PublicPropertyReportSignInStatus = union([
    literal("idle"),
    literal("submitting"),
    literal("link_sent"),
    literal("domain_not_recognised"),
]);
export type TPublicPropertyReportSignInStatus = TTypeOfCodec<typeof PublicPropertyReportSignInStatus>;

export const PublicPropertyReportSignedInContact = required({
    introducer_id: string(),
    introducer_name: string(),
    first_name: string(),
    last_name: string(),
    email: union([nullCodec(), string()]),
    phone_number: union([nullCodec(), string()]),
});
export type TPublicPropertyReportSignedInContact = TTypeOfCodec<typeof PublicPropertyReportSignedInContact>;

export const PublicPropertyReportIntroducerOption = required({
    id: string(),
    name: string(),
});
export type TPublicPropertyReportIntroducerOption = TTypeOfCodec<typeof PublicPropertyReportIntroducerOption>;

export const PublicPropertyReportPage = required({
    selected_address_id: union([nullCodec(), positiveInteger()]),
    visible_title_number: union([nullCodec(), string()]),
    form: PublicPropertyReportAddressSearchForm,
    view: PublicPropertyReportPageView,
    sign_in_email: string(),
    sign_in_status: PublicPropertyReportSignInStatus,
    signed_in_contact: union([nullCodec(), PublicPropertyReportSignedInContact]),
    staff_introducer_options: union([nullCodec(), array(PublicPropertyReportIntroducerOption)]),
    staff_introducer_filter: string(),
    selected_staff_introducer_id: union([nullCodec(), string()]),
});
export type TPublicPropertyReportPageCodec = typeof PublicPropertyReportPage;
